import { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCodeBranch, faPlus, faPen, faTrash, faCheck, faCopy } from '@fortawesome/free-solid-svg-icons';
import { Client } from '../../../context/ClientContext';
import { supabase } from '../../../lib/supabase';
import { Button, Card, IconButton, Input, KebabMenu, SidePanel } from '../../ui';

interface ClientRepo {
  id: string;
  client_id: string;
  name: string;
  repo_url: string;
  description: string | null;
  created_at: string;
  updated_at: string;
}

interface ReposSectionProps {
  client: Client;
  expandedSection: string | null;
  toggleSection: (section: string) => void;
  onRepoChanged?: () => void;
}

export default function ReposSection({ client, expandedSection, onRepoChanged }: ReposSectionProps) {
  const [repos, setRepos] = useState<ClientRepo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddingRepo, setIsAddingRepo] = useState(false);
  const [editingRepoId, setEditingRepoId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [newRepo, setNewRepo] = useState({
    name: '',
    repo_url: '',
    description: '',
  });

  const fetchRepos = useCallback(async () => {
    if (!client?.id) return;

    try {
      setIsLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('client_repos')
        .select('*')
        .eq('client_id', client.id)
        .order('name', { ascending: true });

      if (fetchError) throw fetchError;
      setRepos((data || []) as ClientRepo[]);
    } catch (err) {
      console.error('Error fetching repos:', err);
      setError('Failed to load repos');
    } finally {
      setIsLoading(false);
    }
  }, [client?.id]);

  useEffect(() => {
    if (expandedSection === 'repos') {
      fetchRepos();
    }
  }, [expandedSection, fetchRepos]);

  const validateUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const formatUrl = (url: string): string => {
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return `https://${url}`;
    }
    return url;
  };

  const handleSubmit = async () => {
    if (!newRepo.name.trim() || !newRepo.repo_url.trim()) {
      setError('Name and repository URL are required');
      return;
    }

    const formattedUrl = formatUrl(newRepo.repo_url.trim());
    if (!validateUrl(formattedUrl)) {
      setError('Please enter a valid URL');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      if (editingRepoId) {
        const { error: updateError } = await supabase
          .from('client_repos')
          .update({
            name: newRepo.name.trim(),
            repo_url: formattedUrl,
            description: newRepo.description.trim() || null,
          })
          .eq('id', editingRepoId);

        if (updateError) throw updateError;
        setSuccess('Repository updated!');
      } else {
        const { error: insertError } = await supabase.from('client_repos').insert({
          client_id: client.id,
          name: newRepo.name.trim(),
          repo_url: formattedUrl,
          description: newRepo.description.trim() || null,
        });

        if (insertError) throw insertError;
        setSuccess('Repository added!');
      }

      setNewRepo({ name: '', repo_url: '', description: '' });
      setIsAddingRepo(false);
      setEditingRepoId(null);
      await fetchRepos();
      if (onRepoChanged) onRepoChanged();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving repo:', err);
      setError('Failed to save repository. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (repo: ClientRepo) => {
    setNewRepo({
      name: repo.name,
      repo_url: repo.repo_url,
      description: repo.description || '',
    });
    setEditingRepoId(repo.id);
    setIsAddingRepo(true);
  };

  const handleDelete = async (repoId: string) => {
    if (!confirm('Remove this repository?')) return;

    try {
      setError(null);
      const { error: deleteError } = await supabase.from('client_repos').delete().eq('id', repoId);

      if (deleteError) throw deleteError;
      setSuccess('Repository removed');
      await fetchRepos();
      if (onRepoChanged) onRepoChanged();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting repo:', err);
      setError('Failed to remove repository.');
    }
  };

  const handleCancel = () => {
    setNewRepo({ name: '', repo_url: '', description: '' });
    setIsAddingRepo(false);
    setEditingRepoId(null);
    setError(null);
  };

  const getRepoName = (url: string): string => {
    try {
      const parsed = new URL(url);
      return parsed.pathname.replace(/^\//, '').replace(/\.git$/, '');
    } catch {
      return url;
    }
  };

  const copyUrl = (url: string) => {
    navigator.clipboard.writeText(url).catch((err) => console.error('Failed to copy URL:', err));
  };

  if (expandedSection !== 'repos') return null;

  return (
    <Card
      eyebrow="Repos"
      title={repos.length > 0 ? `${repos.length} connected` : 'No repos connected'}
      actions={
        <Button size="sm" variant="secondary" onClick={() => setIsAddingRepo(true)}>
          <FontAwesomeIcon icon={faPlus} style={{ fontSize: '12px', marginRight: '7px' }} />
          Connect Repo
        </Button>
      }
    >
      {/* Status Messages */}
      {error && !isAddingRepo && (
        <div
          style={{
            marginBottom: '14px',
            padding: '10px 12px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--status-down-soft)',
            border: '1px solid rgba(229, 72, 77, 0.25)',
            color: 'var(--danger-400)',
            fontSize: 'var(--text-sm)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
          }}
        >
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="hover-underline"
            style={{
              border: 'none',
              background: 'transparent',
              color: 'inherit',
              cursor: 'pointer',
              fontSize: 'var(--text-xs)',
              flexShrink: 0,
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      {success && (
        <div
          style={{
            marginBottom: '14px',
            padding: '10px 12px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--status-up-soft)',
            border: '1px solid rgba(86, 162, 104, 0.25)',
            color: 'var(--status-up)',
            fontSize: 'var(--text-sm)',
          }}
        >
          <FontAwesomeIcon icon={faCheck} style={{ fontSize: '11px', marginRight: '8px' }} />
          {success}
        </div>
      )}

      {/* Repos List */}
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '28px 0' }}>
          <div
            className="animate-spin w-7 h-7 border-2 rounded-full"
            style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }}
          />
        </div>
      ) : repos.length === 0 ? (
        <div style={{ padding: '28px 0', textAlign: 'center', color: 'var(--text-muted)' }}>
          <FontAwesomeIcon icon={faCodeBranch} style={{ fontSize: '22px', color: 'var(--text-faint)' }} />
          <p style={{ margin: '12px 0 16px', fontSize: 'var(--text-base)' }}>
            Connect a repository to keep every codebase for this client one click away.
          </p>
          <Button size="sm" variant="secondary" onClick={() => setIsAddingRepo(true)}>
            <FontAwesomeIcon icon={faPlus} style={{ fontSize: '12px', marginRight: '7px' }} />
            Connect Repo
          </Button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {repos.map((repo) => (
            <div
              key={repo.id}
              className="hover-wash"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '10px',
                margin: '0 -10px',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              <FontAwesomeIcon
                icon={faCodeBranch}
                style={{ fontSize: '13px', width: '16px', color: 'var(--text-faint)' }}
              />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span
                  className="t-num"
                  style={{
                    display: 'block',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
                    color: 'var(--text-body)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {repo.name}
                </span>
                <span
                  className="t-num"
                  style={{
                    display: 'block',
                    fontSize: 'var(--text-xs)',
                    color: 'var(--text-faint)',
                    marginTop: '3px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {getRepoName(repo.repo_url)}
                </span>
                {repo.description ? (
                  <span
                    style={{
                      display: 'block',
                      fontSize: 'var(--text-xs)',
                      color: 'var(--text-faint)',
                      marginTop: '2px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {repo.description}
                  </span>
                ) : null}
              </span>
              <IconButton
                icon="arrow-up-right"
                label="Open repo"
                size="sm"
                onClick={() => window.open(repo.repo_url, '_blank', 'noopener,noreferrer')}
              />
              <KebabMenu
                label="Repo actions"
                actions={[
                  { label: 'Copy URL', icon: faCopy, onClick: () => copyUrl(repo.repo_url) },
                  { label: 'Edit Repo', icon: faPen, onClick: () => handleEdit(repo) },
                  { label: 'Delete', icon: faTrash, danger: true, onClick: () => handleDelete(repo.id) },
                ]}
              />
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Repo Panel */}
      <SidePanel
        open={isAddingRepo}
        onClose={handleCancel}
        width={440}
        eyebrow={`Repos · ${client.name}`}
        title={editingRepoId ? 'Edit Repo' : 'Connect Repo'}
        footer={
          <>
            <Button
              icon="check"
              onClick={handleSubmit}
              disabled={isSubmitting || !newRepo.name.trim() || !newRepo.repo_url.trim()}
            >
              {isSubmitting ? 'Saving...' : editingRepoId ? 'Update Repo' : 'Connect Repo'}
            </Button>
            <Button variant="ghost" onClick={handleCancel} disabled={isSubmitting}>
              Cancel
            </Button>
          </>
        }
      >
        <div className="ck-stagger" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {error && (
            <div
              style={{
                padding: '10px 12px',
                borderRadius: 'var(--radius-md)',
                background: 'var(--status-down-soft)',
                border: '1px solid rgba(229, 72, 77, 0.25)',
                color: 'var(--danger-400)',
                fontSize: 'var(--text-sm)',
              }}
            >
              {error}
            </div>
          )}

          <Input
            label="Name"
            value={newRepo.name}
            onChange={(e) => setNewRepo({ ...newRepo, name: e.target.value })}
            placeholder="Frontend site, CMS, admin..."
          />

          <Input
            label="Repository URL"
            type="url"
            value={newRepo.repo_url}
            onChange={(e) => setNewRepo({ ...newRepo, repo_url: e.target.value })}
            placeholder="https://github.com/org/repo"
          />

          <div>
            <div className="t-eyebrow" style={{ marginBottom: '7px' }}>Description</div>
            <textarea
              value={newRepo.description}
              onChange={(e) => setNewRepo({ ...newRepo, description: e.target.value })}
              rows={2}
              placeholder="What this repo is..."
              className="ck-textarea"
            />
          </div>
        </div>
      </SidePanel>
    </Card>
  );
}
